import React, { useState, useEffect } from 'react';
import { ColumnType } from 'antd/lib/table';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';
import { SwitchClickEventHandler } from 'antd/lib/switch';

import './index.less';

import {
  ProviderCreationRequest,
  ProviderModel,
  ProviderModelOriginType,
  ProviderModelType,
} from '../../../../gamma-api';
import { defaultApi } from '../../../apis';
import IbPageTitle from '../../../components/IbPageTitle';
import IbTable from '../../../components/common/IbTable';
import IbTypography from '../../../components/common/IbTypography';
import IbButton from '../../../components/common/IbButton';
import IbIcon from '../../../components/common/IbIcon';
import { alertsSelectorAdd } from '../../../recoil';
import { LARGE_PAGE_SIZE } from '../../../constants';
import IbTag from '../../../components/common/IbTag';
import { ProvidersEmptyLogo } from '../../../assets';
import IbContextMenu, { IIbContextMenuItem } from '../../../components/common/IbContextMenu';
import IbSwitch from '../../../components/common/IbSwitch';
import IbDivider from '../../../components/common/IbDivider';
import IbConfirmModal from '../../../components/common/IbConfirmModal';
import { mask, MaskingMode } from '../../../utils/stringUtil';
import { ValidationErrors } from '../../../types';
import { tryGetValidationErrors } from '../../../utils/errorUtils';

import ProviderEditModal from './ProviderEditModal';
import {
  DEFAULT_PROVIDER,
  PROVIDER_NAMES,
  MODEL_TYPE_NAMES,
  MAX_MODEL_NAMES_TOTAL_LENGTH,
  API_KEY_MASKED_LENGTH,
  API_KEY_VISIBLE_LENGTH,
} from './const';

const MAIN_CLASS_NAME = 'providers-page';
const CONTENT_CLASS_NAME = `${MAIN_CLASS_NAME}__content`;
const EMPTY_CONTENT_CLASS_NAME = `${CONTENT_CLASS_NAME}_empty`;
const EMPTY_CONTENT_PLACEHOLDER_CLASS_NAME = `${EMPTY_CONTENT_CLASS_NAME}__placeholder`;
const TABLE_CLASS_NAME = `${CONTENT_CLASS_NAME}__table`;
const MODELS_CLASS_NAME = `${TABLE_CLASS_NAME}__models`;
const MODELS_DISABLED_CLASS_NAME = `${MODELS_CLASS_NAME}_disabled`;
const ACTIONS_CELL_CLASS_NAME = `${TABLE_CLASS_NAME}__actions-cell`;
const CONTEXT_MENU_CLASS_NAME = `${TABLE_CLASS_NAME}__context-menu`;

const ACTIONS_DATA_INDEX = 'actions';

const ProvidersPage: React.FC = () => {
  const { t } = useTranslation();
  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [providerList, setProviderList] = useState<ProviderModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [editProvider, setEditProvider] = useState<ProviderModel>();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [contextMenuProvider, setContextMenuProvider] = useState<ProviderModel>();
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [providerForDeletion, setProviderForDeletion] = useState<ProviderModel>();
  const [saving, setSaving] = useState(false);

  const providerListIsEmpty = !loading && !providerList.length;

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const response = await defaultApi.searchProvidersGeneralProvidersGet(0, LARGE_PAGE_SIZE, true);
      setProviderList(response.data.items || []);
    } catch (err) {
      addAlert({ content: t('Providers: Failed to load providers'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (provider: ProviderModel) => {
    setEditProvider(provider);
    setEditModalVisible(true);
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  const onAdd = () => openEditModal(DEFAULT_PROVIDER);

  const onTableRowClick = (provider: ProviderModel, column: ColumnType<ProviderModel>) => {
    if (column.dataIndex === ACTIONS_DATA_INDEX) {
      return;
    }

    openEditModal(provider);
  };

  const onEditModalCancel = () => setEditModalVisible(false);

  const onProviderUpdate = async (provider: ProviderModel, onSubmitErrors: (errors: ValidationErrors) => void) => {
    try {
      await defaultApi.updateProviderGeneralProvidersProviderIdPut(provider.provider_id, {
        title: provider.title,
        name: provider.name,
        enabled: provider.enabled,
        settings: provider.settings,
      });

      const response = await defaultApi.searchProvidersGeneralProvidersGet(0, LARGE_PAGE_SIZE, true);
      setProviderList(response.data.items || []);

      setEditModalVisible(false);
      addAlert({ content: t('Providers: Provider updated', { value: provider.title }), type: 'success' });
    } catch (err) {
      const validationErrors = tryGetValidationErrors(err as Error);
      if (validationErrors) {
        onSubmitErrors(validationErrors);
      } else {
        addAlert({ content: t('Providers: Failed to update provider'), type: 'error' });
        // eslint-disable-next-line no-console
        console.error(err);
      }
    }
  };

  const onProviderAdd = async (provider: ProviderModel, onSubmitErrors: (errors: ValidationErrors) => void) => {
    try {
      await defaultApi.createProviderGeneralProvidersPost(provider as ProviderCreationRequest);

      const response = await defaultApi.searchProvidersGeneralProvidersGet(0, LARGE_PAGE_SIZE, true);
      setProviderList(response.data.items || []);

      setEditModalVisible(false);
      addAlert({ content: t('Providers: Provider added', { value: provider.title }), type: 'success' });
    } catch (err) {
      const validationErrors = tryGetValidationErrors(err as Error);
      if (validationErrors) {
        onSubmitErrors(validationErrors);
      } else {
        addAlert({ content: t('Providers: Failed to add provider'), type: 'error' });
        // eslint-disable-next-line no-console
        console.error(err);
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onProviderCheckSelect = (provider: ProviderModel) => async () => {
    // todo: implement check action
  };

  const onProviderEditSelect = (provider: ProviderModel) => () => openEditModal(provider);

  const onProviderDeleteSelect = (provider: ProviderModel) => () => {
    setProviderForDeletion(provider);
    setDeleteConfirmVisible(true);
  };

  const onProviderEnabledSwitchChange = (provider: ProviderModel) => async () => {
    try {
      setSaving(true);
      await defaultApi.updateProviderGeneralProvidersProviderIdPut(provider.provider_id, {
        title: provider.title,
        name: provider.name,
        enabled: !provider.enabled,
        settings: provider.settings,
      });

      const response = await defaultApi.searchProvidersGeneralProvidersGet(0, LARGE_PAGE_SIZE, true);
      setProviderList(response.data.items || []);
    } catch (err) {
      addAlert({ content: t('Providers: Failed to update provider'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const onProviderEnabledSwitchClick: SwitchClickEventHandler = (_, e) => e.stopPropagation();

  const onProviderContextMenuClose = () => setContextMenuProvider(contextMenuProvider);

  const onCancelDeleteConfirmation = () => {
    setDeleteConfirmVisible(false);
    setProviderForDeletion(undefined);
  };

  const onDeleteConfirmButtonClick = async () => {
    if (!providerForDeletion) return;

    try {
      setDeleting(true);
      await defaultApi.deleteOneProviderGeneralProvidersProviderIdDelete(providerForDeletion.provider_id);

      const response = await defaultApi.searchProvidersGeneralProvidersGet(0, LARGE_PAGE_SIZE, true);
      setProviderList(response.data.items || []);

      setDeleteConfirmVisible(false);
      setProviderForDeletion(undefined);
    } catch (err) {
      addAlert({ content: t('Providers: Failed to delete provider'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const columns: ColumnType<ProviderModel>[] = [
    {
      title: t('Providers: Name'),
      render: (_, record) => (
        <IbTypography.Paragraph disabled={!record.enabled} type="secondary">
          {record.title}
        </IbTypography.Paragraph>
      ),
    },
    {
      title: t('Providers: Provider'),
      render: (_, record) => (
        <IbTypography.Paragraph disabled={!record.enabled} type="secondary">
          {PROVIDER_NAMES[record.name]}
        </IbTypography.Paragraph>
      ),
    },
    {
      title: t('Providers: Model types'),
      render: (_, record) => {
        const modelTypes = [];
        if (record.settings.supported_model_types?.includes(ProviderModelType.Llm)) {
          modelTypes.push(MODEL_TYPE_NAMES[ProviderModelType.Llm]);
        }
        if (record.settings.supported_model_types?.includes(ProviderModelType.Embedding)) {
          modelTypes.push(MODEL_TYPE_NAMES[ProviderModelType.Embedding]);
        }
        if (record.settings.supported_model_types?.includes(ProviderModelType.Rerank)) {
          modelTypes.push(MODEL_TYPE_NAMES[ProviderModelType.Rerank]);
        }

        return (
          <IbTypography.Paragraph disabled={!record.enabled} type="secondary">
            {modelTypes.join(', ')}
          </IbTypography.Paragraph>
        );
      },
    },
    {
      title: t('Providers: Additional models'),
      render: (_, record) => {
        const classes = [MODELS_CLASS_NAME];
        if (!record.enabled) {
          classes.push(MODELS_DISABLED_CLASS_NAME);
        }

        const customModels =
          record.settings.models?.filter((m) => m.origin_type === ProviderModelOriginType.Custom) || [];
        const customModelFullNames = customModels.map((m) => `${MODEL_TYPE_NAMES[m.type]}: ${m.name}`);
        let totalLength = 0;
        let moreCount = 0;
        const renderNames = [] as string[];
        for (const name of customModelFullNames) {
          if (totalLength < MAX_MODEL_NAMES_TOTAL_LENGTH) {
            renderNames.push(name);
          } else {
            moreCount += 1;
          }
          totalLength += name.length;
        }

        return (
          <div className={classes.join(' ')}>
            {renderNames.map((name, index) => (
              <IbTag key={index} content={name} />
            ))}
            {moreCount ? (
              <IbTypography.Paragraph type="secondary">
                {t('Providers: more', { value: moreCount })}
              </IbTypography.Paragraph>
            ) : null}
          </div>
        );
      },
    },
    {
      title: t('Providers: API Key'),
      render: (_, record) =>
        mask(record.settings.api_key, MaskingMode.End, API_KEY_MASKED_LENGTH, API_KEY_VISIBLE_LENGTH),
    },
    {
      className: ACTIONS_CELL_CLASS_NAME,
      title: '',
      render: (_, record) => {
        const onOpenMenu = () => setContextMenuProvider(record);

        const menuItems: IIbContextMenuItem[] = [
          {
            icon: <IbIcon iconName="refresh" />,
            text: t('Providers: Check'),
            onSelect: onProviderCheckSelect(record),
          },
          {
            icon: <IbIcon iconName="edit" />,
            text: t('Providers: Edit'),
            onSelect: onProviderEditSelect(record),
          },
          {
            icon: <IbIcon iconName="delete" />,
            text: t('Providers: Delete'),
            onSelect: onProviderDeleteSelect(record),
          },
          {
            icon: undefined,
            text: <IbDivider type="horizontal" />,
          },
          {
            icon: undefined,
            text: (
              <IbTypography.Paragraph type="secondary">
                <IbSwitch
                  checked={record.enabled}
                  loading={saving}
                  onChange={onProviderEnabledSwitchChange(record)}
                  onClick={onProviderEnabledSwitchClick}
                />
                <span>{record.enabled ? t('Providers: Enabled') : t('Providers: Disabled')}</span>
              </IbTypography.Paragraph>
            ),
          },
        ];

        return (
          <IbContextMenu
            className={CONTEXT_MENU_CLASS_NAME}
            menuItems={menuItems}
            visible={contextMenuProvider?.provider_id === record.provider_id}
            onClose={onProviderContextMenuClose}
          >
            <IbButton icon={<IbIcon iconName="more-one" />} type="icon" onClick={onOpenMenu}></IbButton>
          </IbContextMenu>
        );
      },
      dataIndex: ACTIONS_DATA_INDEX,
    },
  ];

  return (
    <div className={MAIN_CLASS_NAME}>
      <IbPageTitle title={t('Providers: Model providers')} onAdd={onAdd} />
      <div className={`${CONTENT_CLASS_NAME} ${providerListIsEmpty ? EMPTY_CONTENT_CLASS_NAME : ''}`}>
        {providerListIsEmpty ? (
          <div className={EMPTY_CONTENT_PLACEHOLDER_CLASS_NAME}>
            <ProvidersEmptyLogo />
            <IbTypography.Paragraph disabled type="secondary">
              {t('Providers: Empty list text')}
            </IbTypography.Paragraph>
            <IbButton icon={<IbIcon iconName="add-one" />} onClick={onAdd}>
              {t('Providers: Add provider')}
            </IbButton>
          </div>
        ) : (
          <IbTable
            className={TABLE_CLASS_NAME}
            columns={columns}
            dataSource={providerList}
            emptyText={<IbTypography.Paragraph disabled>{t('Providers: Nothing found')}</IbTypography.Paragraph>}
            loading={loading}
            rowKey={(record: ProviderModel) => record.provider_id}
            onRowClick={onTableRowClick}
          />
        )}
      </div>
      <ProviderEditModal
        editProvider={editProvider}
        visible={editModalVisible}
        onAdd={onProviderAdd}
        onCancel={onEditModalCancel}
        onUpdate={onProviderUpdate}
      />
      <IbConfirmModal
        footer={
          <>
            <IbButton key="delete" disabled={deleting} onClick={onDeleteConfirmButtonClick}>
              {t('Providers: Delete')}
            </IbButton>
            <IbButton key="cancel" type="secondary" onClick={onCancelDeleteConfirmation}>
              {t('Providers: Cancel')}
            </IbButton>
          </>
        }
        title={t('Providers: Delete provider?')}
        visible={deleteConfirmVisible}
        onCancel={onCancelDeleteConfirmation}
      >
        <IbTypography.Paragraph type="secondary">
          {t('Providers: Are you sure you want to remove the provider?', { value: providerForDeletion?.title })}
        </IbTypography.Paragraph>
      </IbConfirmModal>
    </div>
  );
};

export default ProvidersPage;
